import { GroundType } from 'models/ground';
import React, { useMemo } from 'react';
import {
  Circle, Group, Line, Path,
} from 'react-konva';
import {
  DEFAULT_PITCH_LINE_COLOR,
  DEFAULT_PITCH_STROKEWIDTH,
  FUTSAL_AREA_WIDTH,
  FUTSAL_CENTER_RADIUS,
  FUTSAL_DISK_OFFSET,
  FUTSAL_DISK_RADIUS,
  FUTSAL_GOAL_HEIGTH,
} from '../constants';
import {
  flipX, flipY, describeArc, calculateRealWorldMeter,
} from '../utils';
import Vertices from '../Vertices';
import { GroundGeometry } from '../../../../types/webPlayer';

function Area(props: {
  vertexA: { x: number; y: number };
  vertexC: { x: number; y: number };
  height: number;
  center: { x: number; y: number };
  scale: number;
  flipped?: boolean;
}) {
  const {
    vertexA, vertexC, height, scale, flipped, center,
  } = props;

  const scaledAreaWidth = FUTSAL_AREA_WIDTH * scale;
  const scaledGoalHeight = FUTSAL_GOAL_HEIGTH * scale;
  const scaledDiskOffset = FUTSAL_DISK_OFFSET * scale;
  const scaledDiskRadius = FUTSAL_DISK_RADIUS * scale;
  const scaledCornerRadius = scale;

  const isReversed = vertexA.y < vertexC.y;

  const geometry = useMemo(() => ({
    arc1: {
      x: flipped
        ? (isReversed ? vertexC.x : vertexC.x - scaledCornerRadius)
        : vertexA.x + scaledCornerRadius,
      y: flipped
        ? (isReversed ? vertexC.y : vertexC.y + scaledCornerRadius)
        : (isReversed ? vertexC.y - scaledCornerRadius : vertexC.y + scaledCornerRadius),
      radius: scaledCornerRadius < 0 ? scaledCornerRadius * -1 : scaledCornerRadius,
      startAngle: flipped ? (isReversed ? -90 : 90) : (isReversed ? -90 : 0),
      endAngle: flipped ? (isReversed ? 0 : 0) : (isReversed ? -180 : -90),
    },
    arc2: {
      x: flipped ? vertexC.x - scaledAreaWidth : vertexA.x + scaledAreaWidth,
      y: center.y + scaledAreaWidth + scaledGoalHeight / 2,
      radius: scaledAreaWidth < 0 ? scaledAreaWidth * -1 : scaledAreaWidth,
      startAngle: flipped ? 90 : 0,
      endAngle: flipped ? 0 : -90,
    },
    arc3: {
      x: flipped ? vertexC.x : vertexA.x + scaledAreaWidth,
      y: flipped
        ? center.y - scaledGoalHeight / 2
        : center.y - scaledAreaWidth - scaledGoalHeight / 2,
      radius: scaledAreaWidth < 0 ? scaledAreaWidth * -1 : scaledAreaWidth,
      startAngle: flipped ? 270 : 270,
      endAngle: flipped ? 0 : 180,
    },
    arc4: {
      x: flipped
        ? (isReversed ? vertexC.x - scaledCornerRadius : vertexC.x)
        : (isReversed ? vertexA.x : vertexA.x + scaledCornerRadius),
      y: flipped
        ? (isReversed ? vertexA.y + scaledCornerRadius : vertexA.y)
        : (isReversed ? vertexA.y : vertexA.y - scaledCornerRadius),
      radius: scaledCornerRadius < 0 ? scaledCornerRadius * -1 : scaledCornerRadius,
      startAngle: flipped ? (isReversed ? 90 : 270) : (isReversed ? 90 : 270),
      endAngle: flipped ? (isReversed ? 0 : 0) : (isReversed ? 180 : 180),
    },
    line1: flipped
      ? [
        flipX({ x: vertexA.x + scaledAreaWidth, y: center.y + scaledGoalHeight / 2}, center),
        flipY({ x: vertexA.x + scaledAreaWidth, y: center.y + scaledGoalHeight / 2}, center),
        flipX({ x: vertexA.x + scaledAreaWidth, y: center.y - scaledGoalHeight / 2}, center),
        flipY({ x: vertexA.x + scaledAreaWidth, y: center.y - scaledGoalHeight / 2}, center),
      ]
      : [
        vertexA.x + scaledAreaWidth,
        center.y + scaledGoalHeight / 2,
        vertexA.x + scaledAreaWidth,
        center.y - scaledGoalHeight / 2,
      ],
    circle1: {
      x: flipped ? vertexC.x - scaledDiskOffset : vertexA.x + scaledDiskOffset,
      y: center.y,
      radius: scaledDiskRadius < 0 ? scaledDiskRadius * -1 : scaledDiskRadius,
    },
    circle2: {
      x: flipped ? vertexC.x - scaledAreaWidth : vertexA.x + scaledAreaWidth,
      y: center.y,
      radius: scaledDiskRadius < 0 ? scaledDiskRadius * -1 : scaledDiskRadius,
    },
  }), [vertexA.x, vertexA.y, vertexC.x, vertexC.y, flipped, scaledCornerRadius, height, center]);

  const paths = useMemo(() => ({
    path1: describeArc(
      geometry.arc1.x,
      geometry.arc1.y,
      geometry.arc1.radius,
      geometry.arc1.startAngle,
      geometry.arc1.endAngle,
    ),
    path2: describeArc(
      geometry.arc2.x,
      geometry.arc2.y,
      geometry.arc2.radius,
      geometry.arc2.startAngle,
      geometry.arc2.endAngle,
    ),
    path3: describeArc(
      geometry.arc3.x,
      geometry.arc3.y,
      geometry.arc3.radius,
      geometry.arc3.startAngle,
      geometry.arc3.endAngle,
    ),
    path4: describeArc(
      geometry.arc4.x,
      geometry.arc4.y,
      geometry.arc4.radius,
      geometry.arc4.startAngle,
      geometry.arc4.endAngle,
    ),
  }), [geometry]);

  return (
    <Group>
      <Path data={paths.path1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Path data={paths.path2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={geometry.line1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Circle
        x={geometry.circle1.x}
        y={geometry.circle1.y}
        radius={geometry.circle1.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        fill={DEFAULT_PITCH_LINE_COLOR}
      />
      <Circle
        x={geometry.circle2.x}
        y={geometry.circle2.y}
        radius={geometry.circle2.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        fill={DEFAULT_PITCH_LINE_COLOR}
      />
      <Path data={paths.path3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Path data={paths.path4} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
    </Group>
  );
}

export default function Futsal(props: {
  groundData: GroundType;
  scale: number;
  vertices: boolean;
  groundGeometry: GroundGeometry;
}) {
  const { groundData, groundGeometry, scale, vertices } = props;

  const geometry = useMemo(() => {
    const {dims, vertex, center} = groundGeometry;
    const {width, height} = dims;

    const {
      vertexA,
      vertexB,
      vertexC,
      vertexD,
    } = vertex;

    const scaledCenterRadius = FUTSAL_CENTER_RADIUS * scale;

    return {
      vertex,
      scale,
      center,
      width,
      height,
      line1: [
        vertexA.x, vertexA.y,
        vertexB.x, vertexB.y,
        vertexC.x, vertexC.y,
        vertexD.x, vertexD.y,
      ],
      line2: [
        center.x, vertexA.y,
        center.x, vertexC.y,
      ],
      circle1: {
        x: center.x,
        y: center.y,
        radius: scaledCenterRadius < 0 ? scaledCenterRadius * -1 : scaledCenterRadius,
      },
    };
  }, [groundGeometry]);

  return (
    <Group>
      {/* border */}
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={geometry.line1}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        closed
      />
      {/* left area */}
      <Area
        vertexA={geometry.vertex.vertexA}
        vertexC={geometry.vertex.vertexC}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
      />
      {/* halfcourt */}
      <Line
        points={geometry.line2}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Circle
        x={geometry.circle1.x}
        y={geometry.circle1.y}
        radius={geometry.circle1.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      {/* rightarea */}
      <Area
        vertexA={geometry.vertex.vertexA}
        vertexC={geometry.vertex.vertexC}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
        flipped
      />

      {vertices ? <Vertices A={geometry.vertex.vertexA} C={geometry.vertex.vertexC} /> : null}
    </Group>
  );
}
